import { client } from "@/hono";
import { useMentionOptions } from "@/hooks/use-mention-options";
import { useCitadel } from "@/hooks/use-citadel";
import { useWikiSpacePages } from "@/hooks/use-wiki-space-pages";
import { useWikiSpaces } from "@/hooks/use-wiki-spaces";
import { EditWikiSpaceModal } from "@/modals/wiki/edit-wiki-space-modal";
import { BlankSlate } from "@msuite/ui/blank-slate";
import { Button } from "@msuite/ui/button";
import { Divider } from "@msuite/ui/divider";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon, icon } from "@msuite/ui/icons";
import { TiptapDisplay } from "@msuite/ui/libs/tiptap";
import { useTranslation } from "@msuite/ui/localization";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { StatusIndicator } from "@msuite/ui/status-indicator";
import { responseHandler } from "@msuite/ui/utils";
import { guardString } from "@msuite/utils/strings";
import React from "react";
import { useNavigate } from "react-router";
import { WikiPageLayout } from "./wiki-page-layout";

/** Props Interface */
interface WikiSpaceOverviewProps {
	spaceId: string;
}

/**
 * A space's home, shown while the space is selected and no page is: its name and description in
 * the document's own frame, and its root pages as a list to start reading from. It stands where
 * a blank "select a page" stood, and gives a new space a place to add its first page.
 */
export function WikiSpaceOverview({ spaceId }: WikiSpaceOverviewProps) {
	/** Hooks */
	const { t } = useTranslation();
	const navigate = useNavigate();
	const qc = useQueryClient();
	const { citadel } = useCitadel();
	const editSpaceModal = useDisclosure();
	const mentionsOptions = useMentionOptions();

	/** Query */
	const { wikiSpaces, isLoading } = useWikiSpaces();
	const { wikiSpacePages } = useWikiSpacePages(spaceId);

	/** Functions */
	async function handleOnAddPage() {
		const response = await client.wiki.spaces[":spaceId"].page.$post({
			param: { spaceId },
			json: { parentPageId: null },
		});
		const data = await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate(["wiki-spaces", spaceId, "pages"]) });
		if (data.page) navigate(`/wiki/${spaceId}/${data.page.id}?create=true`);
	}

	/** Computed */
	const space = wikiSpaces?.find((candidate) => candidate.id === spaceId);
	const pages = wikiSpacePages ?? [];
	const canCreate =
		!!space?.orgId && citadel?.can("create", "Wiki", { id: space.id, orgId: space.orgId }) === true;
	const canUpdate =
		!!space?.orgId && citadel?.can("update", "Wiki", { id: space.id, orgId: space.orgId }) === true;

	/** Guard */
	if (isLoading) return <BlankSlate isLoading />;
	if (!space) {
		return (
			<BlankSlate
				Icon={icon("BookOpen")}
				withBorder={false}
				withBackground={false}
				withAccessory={false}
			/>
		);
	}

	/** Render */
	return (
		<React.Fragment>
			<WikiPageLayout>
				<div className="flex min-h-8 flex-row items-center justify-between gap-4">
					<div className="h-stack text-sm opacity-70">
						<Icon
							name="BookOpen"
							size={14}
						/>
						<span>{t("wiki.space")}</span>
					</div>
					{/* The space's own actions, top right like a page's: editing it, and adding to it. */}
					<div className="h-stack shrink-0">
						{canUpdate && (
							<Button
								icon={<Icon name="Edit" />}
								onClick={editSpaceModal.onOpen}
							>
								{t("general.edit")}
							</Button>
						)}
						{canCreate && (
							<Button
								variant="primary"
								icon={<Icon name="Plus" />}
								onClick={handleOnAddPage}
							>
								{t("wiki.add_page")}
							</Button>
						)}
					</div>
				</div>
				<h1 className="mt-4 h-stack gap-3 text-3xl font-semibold leading-tight tracking-tight">
					<StatusIndicator
						color={space.color}
						size="lg"
					/>
					<span>{space.name}</span>
				</h1>
				{space.description && (
					// The description is a document, not a string: the form writes it with the editor the
					// pages use, so it is read with the display the pages use. Printed as text, it was the
					// document's JSON.
					<TiptapDisplay
						json={space.description}
						className="mt-2.5 text-base opacity-70"
						{...mentionsOptions}
					/>
				)}
				<Divider className="my-6" />
				<p className="label">{t("wiki.pages")}</p>
				{pages.length > 0 ? (
					<div className="mt-2 flex flex-col gap-1">
						{pages.map((page) => (
							<button
								key={page.id}
								type="button"
								className="card h-stack cursor-pointer text-left clickable-opacity"
								onClick={() => navigate(`/wiki/${spaceId}/${page.id}`)}
							>
								<Icon
									name="FileText"
									size={14}
									className="shrink-0 opacity-50"
								/>
								<span className="truncate">{guardString(page.title, t("wiki.untitled"))}</span>
							</button>
						))}
					</div>
				) : (
					<p className="mt-2 text-sm opacity-60">{t("wiki.no_pages_yet")}</p>
				)}
			</WikiPageLayout>
			{editSpaceModal.isOpen && (
				<EditWikiSpaceModal
					isOpen={editSpaceModal.isOpen}
					onClose={editSpaceModal.onClose}
					spaceId={spaceId}
				/>
			)}
		</React.Fragment>
	);
}
